import { CameraIcon, Trash2Icon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ACCEPTED_IMAGE_TYPES,
  MAX_CARDS_PER_CONTACT,
  listBusinessCards,
  type BusinessCard,
} from '@/api/businessCards'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { BusinessCardImage } from '@/components/leads/BusinessCardImage'
import { hasPendingCards, type PendingCards } from '@/components/leads/pendingCards'

/**
 * The photographed cards on one contact — front, back, whatever else was worth a picture.
 *
 * Nothing in here is a request. Adding a picture and removing a card are both recorded in the
 * form and carried out when the form is saved, so Cancel really cancels and a removal is as
 * undoable as a typo until the moment Save is pressed. That is also why the two-card limit can be
 * counted here: what is on the server plus what is held, minus what is about to go.
 *
 * At a stand the card is photographed while the person is still standing there, which is why this
 * works on a lead that has not been saved yet — and, on a saved lead, on a contact row that has
 * just been added. Neither has an id to hang a picture on; the save gives them one.
 */
export function BusinessCardStrip({
  leadId,
  contactId,
  pending,
  onChange,
  reloadToken = 0,
}: {
  /** Null while the lead has not been saved yet. */
  leadId: string | null
  /** Null for a contact that has not been saved yet. */
  contactId: string | null
  /** What this contact is carrying, and how to change it. Owned by the form. */
  pending: PendingCards
  onChange: (next: PendingCards) => void
  /**
   * Bumped by the form when a save applied only part of what was pending: the list below is then
   * out of date, and the strip would otherwise show a card that has just been deleted.
   */
  reloadToken?: number
}) {
  const { t } = useTranslation()
  const input = useRef<HTMLInputElement>(null)
  const [saved, setSaved] = useState<BusinessCard[] | null>(null)

  const ready = leadId !== null && contactId !== null

  useEffect(() => {
    if (!ready) {
      return
    }
    const controller = new AbortController()
    listBusinessCards(leadId, contactId, controller.signal)
      .then((loaded) => {
        if (!controller.signal.aborted) {
          setSaved(loaded)
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setSaved([])
        }
      })
    return () => controller.abort()
  }, [ready, leadId, contactId, reloadToken])

  // Made with the render that needs them and released when that render is replaced: an object URL
  // keeps the whole file alive in memory until it is revoked, and a stand takes photographs by the
  // dozen. Derived rather than state, so there is no render showing pictures that have no URL yet.
  const previews = useMemo(() => pending.files.map((file) => URL.createObjectURL(file)), [pending.files])
  useEffect(() => () => previews.forEach((url) => URL.revokeObjectURL(url)), [previews])

  const loading = ready && saved === null
  // A card marked for removal is gone from the strip straight away, exactly as a removed contact
  // row is. Getting it back is what Cancel is for.
  const kept = (saved ?? []).filter((card) => !pending.removedIds.includes(card.id))
  const total = kept.length + pending.files.length

  return (
    <div className="flex flex-wrap items-start gap-3">
      {loading ? (
        <Skeleton className="h-20 w-32" />
      ) : (
        kept.map((card) => (
          <figure key={card.id} className="group relative">
            <BusinessCardImage
              leadId={leadId as string}
              contactId={contactId as string}
              cardId={card.id}
              alt={t('businessCards.alt')}
              className="h-20 w-32"
            />
            <RemoveButton
              label={t('businessCards.delete')}
              onClick={() =>
                onChange({ ...pending, removedIds: [...pending.removedIds, card.id] })
              }
            />
          </figure>
        ))
      )}

      {pending.files.map((file, index) => (
        <figure key={`${file.name}-${file.lastModified}-${index}`} className="group relative">
          <img
            src={previews[index]}
            alt={t('businessCards.alt')}
            className="h-20 w-32 rounded-lg border object-cover"
          />
          <RemoveButton
            label={t('businessCards.delete')}
            onClick={() =>
              onChange({ ...pending, files: pending.files.filter((_, at) => at !== index) })
            }
          />
        </figure>
      ))}

      {/* Gone rather than disabled once both sides are photographed: a disabled button invites a
          click and then explains nothing. The count says why. */}
      {total < MAX_CARDS_PER_CONTACT ? (
        <div className="grid gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => input.current?.click()}
          >
            <CameraIcon />
            {t('businessCards.add')}
          </Button>
          <p className="text-muted-foreground text-xs">{t('businessCards.formats')}</p>
        </div>
      ) : (
        <p className="text-muted-foreground self-center text-xs">
          {t('businessCards.full', { limit: MAX_CARDS_PER_CONTACT })}
        </p>
      )}

      {/* Only once something is waiting: it is then the answer to "the card is still there?" and
          to "the card is gone already?", which are the same question asked from both sides. */}
      {hasPendingCards(pending) && (
        <p className="text-muted-foreground basis-full text-xs">{t('businessCards.appliedOnSave')}</p>
      )}

      <input
        ref={input}
        type="file"
        // `capture` asks a phone for the camera rather than the gallery — the common case at a
        // stand. A desktop browser ignores it and opens the file picker.
        capture="environment"
        accept={ACCEPTED_IMAGE_TYPES}
        className="hidden"
        aria-label={t('businessCards.add')}
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) {
            onChange({ ...pending, files: [...pending.files, file] })
          }
          // Cleared so that picking the same file again still fires a change event.
          event.target.value = ''
        }}
      />
    </div>
  )
}

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button
      type="button"
      variant="destructive"
      size="icon-sm"
      className="absolute -top-2 -right-2 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
      aria-label={label}
      onClick={onClick}
    >
      <Trash2Icon />
    </Button>
  )
}
